import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiAlertCircle, FiTag } from "react-icons/fi"
import ConfirmationModal from "../../common/ConfirmationModal"
import {
  getAdminCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../services/operations/adminAPI"

const EMPTY_FORM = { name: "", description: "" }

export default function ManageCategories() {
  const { token } = useSelector((state) => state.auth)

  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  // inline create form
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_FORM)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState("")

  // inline edit state — keyed by _id
  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState(EMPTY_FORM)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState("")

  // delete confirmation
  const [confirmModal, setConfirmModal] = useState(null)

  const load = async () => {
    setLoading(true)
    const data = await getAdminCategories(token)
    setCategories(data)
    setLoading(false)
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── CREATE ──────────────────────────────────────── */
  const handleCreate = async () => {
    if (!createForm.name.trim()) { setCreateError("Name is required"); return }
    setCreateLoading(true)
    const ok = await createCategory({ name: createForm.name.trim(), description: createForm.description.trim() }, token)
    setCreateLoading(false)
    if (ok) {
      setShowCreate(false)
      setCreateForm(EMPTY_FORM)
      setCreateError("")
      await load()
    }
  }

  /* ── EDIT ────────────────────────────────────────── */
  const startEdit = (cat) => {
    setEditId(cat._id)
    setEditForm({ name: cat.name, description: cat.description || "" })
    setEditError("")
  }

  const cancelEdit = () => { setEditId(null); setEditError("") }

  const handleUpdate = async () => {
    if (!editForm.name.trim()) { setEditError("Name is required"); return }
    setEditLoading(true)
    const updated = await updateCategory(
      { categoryId: editId, name: editForm.name.trim(), description: editForm.description.trim() },
      token
    )
    setEditLoading(false)
    if (updated) {
      setCategories((prev) => prev.map((c) => (c._id === editId ? updated : c)))
      setEditId(null)
    }
  }

  /* ── DELETE ──────────────────────────────────────── */
  const confirmDelete = (cat) => {
    setConfirmModal({
      text1: `Delete "${cat.name}"?`,
      text2: cat.courses?.length
        ? `This category has ${cat.courses.length} course(s). You must reassign them before deleting.`
        : "This action cannot be undone.",
      btn1Text: "Delete",
      btn2Text: "Cancel",
      btn1Handler: async () => {
        setConfirmModal(null)
        const ok = await deleteCategory(cat._id, token)
        if (ok) setCategories((prev) => prev.filter((c) => c._id !== cat._id))
      },
      btn2Handler: () => setConfirmModal(null),
    })
  }

  /* ── UI ──────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-richblack-5">Categories</h1>
          <p className="text-sm text-richblack-400">
            {loading ? "Loading…" : `${categories.length} categor${categories.length === 1 ? "y" : "ies"}`}
          </p>
        </div>
        {!showCreate && (
          <button
            onClick={() => { setShowCreate(true); setCreateError("") }}
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-2.5 text-sm font-bold text-richblack-900 transition-colors hover:bg-yellow-100"
          >
            <FiPlus size={15} /> New Category
          </button>
        )}
      </div>

      {/* Inline create form */}
      {showCreate && (
        <div className="rounded-xl border border-yellow-50/30 bg-richblack-800 p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-semibold text-yellow-50">New Category</p>
            <button
              onClick={() => { setShowCreate(false); setCreateForm(EMPTY_FORM); setCreateError("") }}
              className="rounded-lg p-1.5 text-richblack-400 hover:bg-richblack-700 hover:text-richblack-5"
            >
              <FiX size={15} />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <input
                autoFocus
                value={createForm.name}
                onChange={(e) => { setCreateForm((f) => ({ ...f, name: e.target.value })); setCreateError("") }}
                onKeyDown={(e) => e.key === "Enter" && !createLoading && handleCreate()}
                placeholder="Category name *"
                className="form-style w-full"
              />
              {createError && (
                <p className="mt-1 flex items-center gap-1 text-xs text-pink-200">
                  <FiAlertCircle size={12} /> {createError}
                </p>
              )}
            </div>
            <textarea
              rows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Description (optional)"
              className="form-style w-full resize-none"
            />
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <button
              disabled={createLoading}
              onClick={() => { setShowCreate(false); setCreateForm(EMPTY_FORM); setCreateError("") }}
              className="rounded-xl border border-richblack-600 px-4 py-2 text-sm text-richblack-300 hover:text-richblack-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              disabled={createLoading || !createForm.name.trim()}
              onClick={handleCreate}
              className="flex items-center gap-1.5 rounded-xl bg-yellow-50 px-5 py-2 text-sm font-bold text-richblack-900 hover:bg-yellow-100 disabled:opacity-60"
            >
              {createLoading ? "Creating…" : <><FiCheck size={14} /> Create</>}
            </button>
          </div>
        </div>
      )}

      {/* Categories list */}
      {loading ? (
        <div className="space-y-3">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-richblack-800" />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-24">
          <FiTag size={40} className="text-richblack-600" />
          <p className="text-richblack-400">No categories yet. Create one above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((cat) => (
            <div
              key={cat._id}
              className="rounded-xl border border-richblack-700 bg-richblack-800 transition-colors hover:border-richblack-600"
            >
              {editId === cat._id ? (
                /* ── Edit mode ── */
                <div className="p-4">
                  <div className="space-y-3">
                    <div>
                      <input
                        autoFocus
                        value={editForm.name}
                        onChange={(e) => { setEditForm((f) => ({ ...f, name: e.target.value })); setEditError("") }}
                        onKeyDown={(e) => e.key === "Enter" && !editLoading && handleUpdate()}
                        placeholder="Category name *"
                        className="form-style w-full"
                      />
                      {editError && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-pink-200">
                          <FiAlertCircle size={12} /> {editError}
                        </p>
                      )}
                    </div>
                    <textarea
                      rows={2}
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Description (optional)"
                      className="form-style w-full resize-none"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-richblack-500">
                      {cat.courses?.length ?? 0} course{cat.courses?.length !== 1 ? "s" : ""}
                    </span>
                    <div className="flex gap-2">
                      <button
                        disabled={editLoading}
                        onClick={cancelEdit}
                        className="rounded-lg border border-richblack-600 px-3 py-1.5 text-xs text-richblack-300 hover:text-richblack-100 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={editLoading || !editForm.name.trim()}
                        onClick={handleUpdate}
                        className="flex items-center gap-1 rounded-lg bg-yellow-50 px-4 py-1.5 text-xs font-bold text-richblack-900 hover:bg-yellow-100 disabled:opacity-60"
                      >
                        {editLoading ? "Saving…" : <><FiCheck size={12} /> Save</>}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div className="flex items-start gap-3 p-4">
                  <div className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-yellow-50/10">
                    <FiTag size={16} className="text-yellow-50" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-richblack-5">{cat.name}</p>
                    {cat.description && (
                      <p className="mt-0.5 text-sm text-richblack-400 line-clamp-2">{cat.description}</p>
                    )}
                    <p className="mt-1.5 text-xs text-richblack-500">
                      {cat.courses?.length ?? 0} course{cat.courses?.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={() => startEdit(cat)}
                      className="rounded-lg p-2 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-5"
                      title="Edit"
                    >
                      <FiEdit2 size={15} />
                    </button>
                    <button
                      onClick={() => confirmDelete(cat)}
                      className="rounded-lg p-2 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-pink-300"
                      title="Delete"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmModal && <ConfirmationModal modalData={confirmModal} />}
    </div>
  )
}
